import { useEffect, useState } from "react"
import { Calendar, X } from "lucide-react"
import type { ActionPlan } from "./types"

/**
 * Inline Slot-Edit-Modal fuer den Review-Plan-Workflow.
 *
 * Im Gegensatz zum BookingModal (das direkt in HERO bucht und einen
 * hero_project_id braucht) aendert dieses Modal nur den Slot **im Plan**
 * (Datum/Uhrzeit/Dauer). Beim Senden wird der Plan dann via /execute
 * komplett ausgefuehrt — inkl. Kontakt+Projekt+Termin-Anlage mit dem
 * geaenderten Slot. So funktioniert das Editieren auch bei Mails fuer
 * die noch kein Projekt im HERO existiert.
 */
export function SlotEditModal({
  open, onClose, slot, label, onSave,
}: {
  open: boolean
  onClose: () => void
  slot: ActionPlan["slot"]
  label: string
  onSave: (next: ActionPlan["slot"]) => void
}) {
  const [date, setDate] = useState("")
  const [time, setTime] = useState("")
  const [duration, setDuration] = useState(45)

  useEffect(() => {
    if (!open) return
    const sdt = (slot?.start_datetime || "").trim()
    if (sdt) {
      // Plan speichert Berlin-Lokalzeit als naive ISO ("2026-04-30T08:00:00")
      const [d, t] = sdt.split("T")
      setDate(d || "")
      setTime((t || "").slice(0, 5))
    } else {
      // Default: morgen 09:00
      const tomorrow = new Date(Date.now() + 24 * 3600_000)
      const yyyy = tomorrow.getFullYear()
      const mm = String(tomorrow.getMonth() + 1).padStart(2, "0")
      const dd = String(tomorrow.getDate()).padStart(2, "0")
      setDate(`${yyyy}-${mm}-${dd}`)
      setTime("09:00")
    }
    setDuration(slot?.duration_minutes || 45)
  }, [open, slot])

  const handleSave = () => {
    if (!date || !time) return
    const start_datetime = `${date}T${time}:00`
    onSave({
      ...(slot || { start_datetime: "", duration_minutes: 45, partner_id: null }),
      start_datetime,
      duration_minutes: duration,
    })
    onClose()
  }

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(0,0,0,.25)",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <Calendar size={16} color="#3C3489" />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{label} bearbeiten</h3>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ fontSize: 12, color: "#5F5E5A", margin: 0 }}>
            Slot wird im Plan aktualisiert. Termin wird beim Senden in HERO angelegt
            (zusammen mit Kontakt + Projekt).
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label className="pp-form-label">Datum</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                style={{ fontSize: 13 }} autoFocus />
            </div>
            <div>
              <label className="pp-form-label">Uhrzeit</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                style={{ fontSize: 13 }} step={300} />
            </div>
          </div>
          <div>
            <label className="pp-form-label">Dauer (Min)</label>
            <input type="number" value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 45)}
              min={15} max={480} step={15}
              style={{ fontSize: 13, maxWidth: 120 }} />
          </div>
        </div>

        <div style={{
          padding: "12px 20px", borderTop: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", gap: 8, justifyContent: "flex-end",
        }}>
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm">
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!date || !time}
            style={{
              background: "#3C3489", color: "#fff", border: "none",
              padding: "8px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500,
              cursor: "pointer",
            }}
          >
            ✓ Speichern
          </button>
        </div>
      </div>
    </div>
  )
}
